
import React from "react";
import { useTranslations } from "use-intl";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import FilterPanel from "@/components/shared/FilterPanel";
import AmountRangeFilterFields from "@/components/shared/AmountRangeFilterFields";
import { ReceivableFilters } from "@/types/payments";

interface ReceivableFiltersProps {
  filters: ReceivableFilters;
  handleFilterChange: (key: keyof ReceivableFilters, value: string) => void;
  resetFilters: () => void;
  uniqueCurrencies: string[];
  showFilters: boolean;
  setShowFilters: (show: boolean) => void;
}

const ReceivableFiltersComponent = ({
  filters,
  handleFilterChange,
  resetFilters,
  uniqueCurrencies,
  showFilters,
  setShowFilters,
}: ReceivableFiltersProps) => {
  const t = useTranslations("consultantReceivables.filters");
  const tShared = useTranslations("consultantPayables.filters");
  const tStatus = useTranslations("consultantPayables.status");
  return (
    <FilterPanel
      showFilters={showFilters}
      setShowFilters={setShowFilters}
      resetFilters={resetFilters}
      labels={{ show: tShared("show"), hide: tShared("hide"), reset: tShared("reset") }}
    >
      {/* Status Filter */}
      <div className="space-y-2">
        <label className="text-sm font-medium">{tShared("status")}</label>
        <Select
          value={filters.status}
          onValueChange={(value) => handleFilterChange('status', value)}
        >
          <SelectTrigger>
            <SelectValue placeholder={t("statusPlaceholder")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{tShared("allStatuses")}</SelectItem>
            <SelectItem value="unpaid">{tStatus("unpaid")}</SelectItem>
            <SelectItem value="overdue">{tStatus("overdue")}</SelectItem>
            <SelectItem value="paid">{tStatus("paid")}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Customer Filter */}
      <div className="space-y-2">
        <label className="text-sm font-medium">{t("customer")}</label>
        <Input
          placeholder={t("customerPlaceholder")}
          value={filters.customer}
          onChange={(e) => handleFilterChange('customer', e.target.value)}
        />
      </div>

      <AmountRangeFilterFields
        minAmount={filters.minAmount}
        maxAmount={filters.maxAmount}
        onChange={handleFilterChange}
        labels={{ min: t("minAmount"), max: t("maxAmount") }}
        placeholders={{ min: t("minAmountPlaceholder"), max: t("maxAmountPlaceholder") }}
      />

      {/* Currency Filter */}
      <div className="space-y-2">
        <label className="text-sm font-medium">{t("currency")}</label>
        <Select
          value={filters.currency}
          onValueChange={(value) => handleFilterChange('currency', value)}
        >
          <SelectTrigger>
            <SelectValue placeholder={t("currencyPlaceholder")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{tShared("allCurrencies")}</SelectItem>
            {uniqueCurrencies.map(currency => (
              <SelectItem key={currency} value={currency}>{currency}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Matched Filter */}
      <div className="space-y-2">
        <label className="text-sm font-medium">{t("matched")}</label>
        <Select
          value={filters.matched as string}
          onValueChange={(value) => {
            handleFilterChange('matched', value);
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder={t("matchedPlaceholder")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("matchedAll")}</SelectItem>
            <SelectItem value="true">{t("matchedYes")}</SelectItem>
            <SelectItem value="false">{t("matchedNo")}</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </FilterPanel>
  );
};

export default ReceivableFiltersComponent;
